<template>
  <div class="product-page__base-section" :class="section.class">
    <h2
      v-if="section.title"
      class="base-section__title hidden-md-up"
      :class="{
        'text--center': !section.secondaryImage || section.mainVideo,
        hidden: section.secondaryImage && !section.mainVideo,
      }"
    >
      {{ section.title }}
    </h2>
    <div
      class="base-section__content-wrapper"
      :class="{
        'base-section__content-wrapper--reverse': section.contentReverse,
        'base-section__content-wrapper--gradient': section.backgroundColor,
      }"
    >
      <template v-if="section.mainVideo">
        <div class="base-section__main-video">
          <sib-video
            :style="'width:100%'"
            :src="section.mainVideo + '#t=0.1'"
            :preload="'metadata'"
            :autoplay="false"
            :loop="false"
            :muted="false"
          ></sib-video>
        </div>
      </template>
      <template
        v-else-if="
          section.productTypeSellingPointByProduct &&
          transformedProductImageNo1 &&
          transformedProductImageNo2
        "
      >
        <div
          class="base-section__image base-section__image--product-type-selling-point"
        >
          <sib-image v-bind="transformedProductImageNo1"></sib-image>
          <sib-image v-bind="transformedProductImageNo2"></sib-image>
        </div>
      </template>
      <template v-else>
        <div class="base-section__image">
          <sib-image v-bind="transformedImage"></sib-image>
          <div
            class="base-section__secondary-image hidden-md-up"
            v-if="section.secondaryImage"
          >
            <sib-image v-bind="transformedSecondaryImage"></sib-image>
          </div>
        </div>
      </template>

      <div class="base-section__content">
        <h2
          v-if="section.title"
          class="base-section__title"
          :class="{
            'hidden-md-down': !section.secondaryImage || section.mainVideo,
            'text--right': section.secondaryImage,
            'mr-sm mt-0': section.secondaryImage,
          }"
        >
          {{ section.title }}
        </h2>
        <div
          class="base-section__content-text"
          v-if="section.content"
          v-html="section.content"
        ></div>
        <div
          class="base-section__video px-sm hidden-md-up mt-sm"
          v-if="section.video"
        >
          <sib-video
            :style="'width:100%'"
            :src="section.video + '#t=0.1'"
            :preload="'metadata'"
            :autoplay="false"
            :loop="false"
            :muted="false"
          ></sib-video>
        </div>
        <div
          class="base-section__cta-button"
          v-if="section.ctaButton"
          @click="scrollToAddToCartButton"
        >
          {{ section.ctaButton }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useDeviceStore from '@/store/device'
import useProductStore from '@/store/product'

const deviceStore = useDeviceStore()
const productStore = useProductStore()

const props = withDefaults(defineProps<{ section: any }>(), {})

const transformedImage = computed(() => {
  return {
    width: 3573,
    height: 1181,
    src:
      (deviceStore.isPhone
        ? props.section.image
        : props.section.desktopImage) || '',
    alt: props.section.title || '',
    title: props.section.title || '',
    sources: [
      {
        size: 330,
      },
      {
        size: 540,
        media: 361,
      },
      {
        size: 720,
        media: 580,
      },
      {
        size: 400,
        media: 768,
      },
      {
        size: 460,
        media: 992,
      },
      {
        size: 550,
        media: 1200,
      },
      {
        size: 750,
        media: 1400,
      },
    ],
    generateSrcSet: true,
  }
})

const transformedProductImageNo1 = computed(() => {
  if (!product.value || !product.value.images?.length) return ''
  return {
    width: 625,
    height: 625,
    src: product.value.images[0].src || '',
    alt: product.value.title || '',
    title: product.value.title || '',
    sources: [
      {
        size: 240,
      },
      {
        size: 350,
        media: 361,
      },
      {
        size: 440,
        media: 580,
      },
      {
        size: 300,
        media: 768,
      },
      {
        size: 360,
        media: 992,
      },
      {
        size: 625,
        media: 1250,
      },
    ],
    generateSrcSet: true,
  }
})

const transformedProductImageNo2 = computed(() => {
  if (!product.value || !product.value.images?.length) return ''
  return {
    width: 625,
    height: 625,
    src: product.value.images[1].src || '',
    alt: product.value.title || '',
    title: product.value.title || '',
    sources: [
      {
        size: 240,
      },
      {
        size: 350,
        media: 361,
      },
      {
        size: 440,
        media: 580,
      },
      {
        size: 300,
        media: 768,
      },
      {
        size: 360,
        media: 992,
      },
      {
        size: 625,
        media: 1250,
      },
    ],
    generateSrcSet: true,
  }
})

const transformedSecondaryImage = computed(() => {
  return {
    width: 3573,
    height: 1181,
    src: props.section.secondaryImage || '',
    alt: props.section.title || '',
    title: props.section.title || '',
    sources: [
      {
        size: 330,
      },
    ],
    generateSrcSet: true,
  }
})

const backgroundColor = computed(() => {
  return props.section?.backgroundColor || '#f7f7f7'
})

const product = computed(() => {
  return productStore.product
})

function scrollToAddToCartButton() {
  const buttonGroup = document.getElementById('product-button-group')
  if (!buttonGroup) return
  window.scrollTo({ top: buttonGroup.offsetTop - 400, behavior: 'smooth' })
}
</script>

<style lang="scss">
.base-section__title {
  font-weight: 700;
  font-size: 22px;
  @include media-md-up() {
    margin-top: 0;
    font-size: 2rem;
    text-align: center;
  }
}
.product-page__base-section {
  .base-section {
    &__content-wrapper {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-areas:
        'a'
        'b';
      gap: 1rem;
      padding-bottom: 2rem;
      @include media-md-up() {
        padding-bottom: unset;
        gap: 2rem;
        grid-template-columns: 1fr 1fr;
        grid-template-areas: 'a b';
      }
    }
    &__content-wrapper--gradient {
      background: #ffffff;
      background: linear-gradient(
        180deg,
        #ffffff 60%,
        v-bind(backgroundColor) 100%
      );
      @include media-md-up() {
        background: unset;
      }
    }
    &__content-wrapper--reverse {
      @include media-md-up() {
        grid-template-areas: 'b a';
      }
    }
    &__image {
      position: relative;
      grid-area: a;
      img {
        border-radius: 0.5rem;
      }
      &--product-type-selling-point {
        position: relative;
        aspect-ratio: 1;

        --color-border: #7d7b79;
        div:first-child,
        div:last-child {
          position: absolute;
          width: 55%;
          border-radius: 0.5rem;
          border-bottom: 2px solid var(--color-border);
          border-left: 2px solid var(--color-border);
        }
        div:first-child {
          transform: translateX(5%) translateY(0%);
        }
        div:last-child {
          transform: translateX(75%) translateY(80%);
        }

        @include media-md-up() {
          div:first-child,
          div:last-child {
            width: 50%;
          }
          div:first-child {
            transform: translateX(10%) translateY(10%);
          }
          div:last-child {
            transform: translateX(90%) translateY(90%);
          }
        }
      }
    }
    &__secondary-image {
      width: 35%;
      position: absolute;
      bottom: -45px;
      left: 1rem;
    }
    &__content {
      margin: auto;
      text-align: justify;
      grid-area: b;
      p {
        margin: 0;
      }
    }
    &__cta-button {
      border: 1px solid;
      border-radius: 20px;
      padding: 6px 1rem;
      width: max-content;
      cursor: pointer;
      margin: 1rem auto 0;
      font-weight: 600;
      &:hover {
        background: #f3f3f3;
      }
    }
    &__content-text {
      padding: 0 1rem;
      @include media-md-up() {
        padding: 0;
      }
    }
  }
}
</style>
